import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { C } from 'src/app/@shared/constants';
import { ExternalReturnValue } from 'src/app/@core/types';
import { Locale } from 'date-fns';
import { StorageService } from 'src/app/@core/storage.service';

@Injectable({
  providedIn: 'root',
})
export class LanguageService {
  public language: BehaviorSubject<[string, string[]]> = new BehaviorSubject(null);

  private storage = inject(StorageService);
  private translate = inject(TranslateService);

  // TODO: provide correct type
  private dateLocales: { [key: string]: any } = {};

  public observeLanguage(): Observable<[string, string[]]> {
    return this.language.asObservable();
  }

  public async init(): Promise<void> {
    const language = this.getLanguage();

    this.language.next([language, C.availableLanguages]);

    this.translate.setDefaultLang(C.defaultLanguage);

    await this.initDateLocales();

    try {
      this.translate.use(language);
    } catch (error) {
      console.error('Error setting language', error);
    }
  }

  public changeLanguage(newLanguage: string): Promise<string | void> {
    // eslint-disable-next-line consistent-return
    return new Promise((resolve, reject) => {
      if (!C.availableLanguages.includes(newLanguage)) {
        // eslint-disable-next-line prefer-promise-reject-errors,no-promise-executor-return
        return reject(`ERROR: '${newLanguage}' is not a language that is available.`);
      }

      this.storage.set('language', newLanguage);
      this.translate.use(newLanguage);
      this.language.next([newLanguage, C.availableLanguages]);

      resolve();
    });
  }

  public getTranslation(translatableString: string): Observable<ExternalReturnValue> {
    this.translate.getTranslation(this.getLanguage());

    return this.translate.get(translatableString);
  }

  public getLanguage(): string {
    const storedLanguage = this.getStoredLanguage();
    const [browserLanguage] = navigator.language.split('-');

    let language = storedLanguage || C.defaultLanguage;

    if (!storedLanguage && C.availableLanguages.includes(browserLanguage)) {
      language = browserLanguage;
    }

    return language;
  }

  public async initDateLocales(): Promise<void> {
    this.dateLocales = await import('date-fns/locale');

    return Promise.resolve();
  }

  public getDateLocaleByLanguage(language: string): Pick<Locale, keyof Locale> {
    const locale = language === 'en'
      ? 'enUS'
      : language;

    // initDateLocales
    if (!this.dateLocales[locale]) {
      console.error('date locale not found', locale, 'ensure locale is valid and initDateLocales has been called before');
    }

    return this.dateLocales[locale];
  }

  private getStoredLanguage(): string {
    const storedLanguage = this.storage.get('language') as string;

    if (!storedLanguage || !C.availableLanguages.includes(storedLanguage)) {
      this.storage.remove('language');

      return '';
    }

    return storedLanguage;
  }
}
